<template>
  <div class="ai-kb">
    <div v-if="loading" class="ai-kb__state">
      <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des accords…
    </div>
    <div v-else-if="loadError" class="ai-kb__state ai-kb__state--error">
      <span>{{ loadError }}</span>
      <button type="button" @click="loadDocuments">Réessayer</button>
    </div>
    <div v-else class="ai-kb__scroll">
      <table class="ai-kb__table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key" :class="column.className" :aria-sort="ariaSort(column)">
              <button type="button" class="ai-kb__sort" @click="toggleSort(column.key)">
                <span>{{ column.label }}</span>
                <i :class="sortIcon(column.key)" aria-hidden="true"></i>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="document in displayedDocuments" :key="document.doc_uuid">
            <td>
              <span class="ai-kb__title" :title="document.title">{{ document.title || '—' }}</span>
              <span v-if="document.is_shared" class="ai-kb__shared" title="Document commun à tous les domaines">Commun</span>
            </td>
            <td>
              <span class="ai-doc-type" :class="`ai-doc-type--${document.doc_type}`">{{ docTypeLabel(document.doc_type) }}</span>
            </td>
            <td>{{ formatDate(document.created_at) }}</td>
            <td>
              <span class="ai-status" :class="`ai-status--${document.status}`">
                <span aria-hidden="true"></span>
                {{ statusLabel(document.status) }}
              </span>
            </td>
          </tr>
          <tr v-if="!displayedDocuments.length">
            <td colspan="4" class="ai-kb__state">
              {{ documents.length ? 'Aucun accord ne correspond à votre recherche.' : 'Aucun accord n’est encore intégré pour ce domaine.' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer v-if="!loading && !loadError" class="ai-kb__footer">
      <span>{{ countLabel }}</span>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import aiService from '@/services/ai.service'
import { DOC_TYPE_LABELS, DOCUMENT_STATUS_LABELS } from './docTypes'

const COLUMNS = [
  { key: 'title', label: 'Titre', className: 'ai-kb__col-title' },
  { key: 'doc_type', label: 'Type', className: 'ai-kb__col-type' },
  { key: 'created_at', label: 'Date d’ajout', className: 'ai-kb__col-date' },
  { key: 'status', label: 'Statut', className: 'ai-kb__col-status' }
]

const STATUS_ORDER = ['ready', 'processing', 'failed', 'expired', 'inactive']

export default {
  name: 'app-ai-assistant-knowledge',
  props: {
    // Free-text filter typed in the page toolbar.
    search: { type: String, default: '' }
  },
  data () {
    return {
      documents: [],
      loading: true,
      loadError: '',
      columns: COLUMNS,
      sort: { key: 'created_at', direction: 'desc' }
    }
  },
  computed: {
    ...mapGetters(['domain']),
    filteredDocuments () {
      const search = this.normalize(this.search)
      if (!search) {
        return this.documents
      }
      return this.documents.filter(document => {
        const haystack = this.normalize([
          document.title,
          this.docTypeLabel(document.doc_type),
          this.statusLabel(document.status),
          document.is_shared ? 'commun' : ''
        ].filter(Boolean).join(' '))
        return haystack.includes(search)
      })
    },
    displayedDocuments () {
      const documents = [...this.filteredDocuments]
      const factor = this.sort.direction === 'asc' ? 1 : -1
      documents.sort((left, right) => {
        const a = this.sortValue(left, this.sort.key)
        const b = this.sortValue(right, this.sort.key)
        if (a === b) {
          return 0
        }
        if (a === null || a === undefined || a === '') {
          return 1
        }
        if (b === null || b === undefined || b === '') {
          return -1
        }
        if (typeof a === 'string' && typeof b === 'string') {
          return a.localeCompare(b, 'fr', { sensitivity: 'base' }) * factor
        }
        return (a > b ? 1 : -1) * factor
      })
      return documents
    },
    countLabel () {
      const total = this.documents.length
      const shown = this.filteredDocuments.length
      const noun = total > 1 ? 'accords' : 'accord'
      if (shown === total) {
        return `${total} ${noun}`
      }
      return `${shown} sur ${total} ${noun}`
    }
  },
  watch: {
    'domain.uuid' () {
      this.loadDocuments()
    }
  },
  created () {
    this.loadDocuments()
  },
  methods: {
    loadDocuments () {
      if (!this.domain || !this.domain.uuid) {
        return
      }
      this.loading = true
      this.loadError = ''
      aiService.listDomainDocuments().then(data => {
        this.documents = Array.isArray(data) ? data : []
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.loadError = status === 403
          ? 'Vous n’avez pas le rôle requis pour consulter la base de connaissances.'
          : 'Impossible de charger les accords intégrés.'
        this.documents = []
      }).finally(() => {
        this.loading = false
      })
    },
    toggleSort (key) {
      if (this.sort.key === key) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sort = { key, direction: key === 'created_at' ? 'desc' : 'asc' }
    },
    sortValue (document, key) {
      switch (key) {
        case 'doc_type':
          return this.docTypeLabel(document.doc_type)
        case 'status': {
          const index = STATUS_ORDER.indexOf(document.status)
          return index === -1 ? STATUS_ORDER.length : index
        }
        case 'created_at':
          return document.created_at ? new Date(document.created_at).getTime() : null
        default:
          return document[key] || ''
      }
    },
    ariaSort (column) {
      if (this.sort.key !== column.key) {
        return 'none'
      }
      return this.sort.direction === 'asc' ? 'ascending' : 'descending'
    },
    sortIcon (key) {
      if (this.sort.key !== key) {
        return 'fas fa-sort'
      }
      return this.sort.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    docTypeLabel (type) {
      return DOC_TYPE_LABELS[type] || type || 'Document'
    },
    statusLabel (status) {
      return DOCUMENT_STATUS_LABELS[status] || status || '—'
    },
    normalize (value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
    },
    formatDate (value) {
      if (!value) {
        return '—'
      }
      const date = new Date(value)
      return isNaN(date.getTime()) ? '—' : format(date, 'dd/MM/yyyy')
    }
  }
}
</script>

<style scoped>
.ai-kb {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.ai-kb__scroll {
  overflow: auto;
}

.ai-kb__table {
  width: 100%;
  min-width: 720px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.ai-kb__table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.75rem 0.875rem;
  background: #f8fafc;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
}

.ai-kb__sort {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.2rem 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
}

.ai-kb__sort:hover,
.ai-kb__sort:focus-visible {
  color: #2563eb;
}

.ai-kb__sort i {
  color: #9ca3af;
}

.ai-kb__table td {
  padding: 0.75rem 0.875rem;
  border-top: 1px solid #edf0f3;
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.25rem;
  vertical-align: middle;
}

.ai-kb__table tbody tr:hover {
  background: #f8fafc;
}

.ai-kb__col-type {
  width: 12rem;
}

.ai-kb__col-date {
  width: 9rem;
}

.ai-kb__col-status {
  width: 11rem;
}

.ai-kb__title {
  display: inline-block;
  max-width: 100%;
  color: #1f2937;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.ai-kb__shared {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.1rem 0.5rem;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  vertical-align: middle;
}

.ai-doc-type {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
}

.ai-doc-type--accord,
.ai-doc-type--avenant {
  background: #dbeafe;
  color: #1d4ed8;
}

.ai-doc-type--ccn {
  background: #ede9fe;
  color: #6d28d9;
}

.ai-doc-type--code_travail {
  background: #fef3c7;
  color: #92400e;
}

.ai-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.ai-status > span {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #94a3b8;
}

.ai-status--ready {
  background: #dcfce7;
  color: #166534;
}

.ai-status--ready > span {
  background: #22c55e;
}

.ai-status--processing {
  background: #dbeafe;
  color: #1d4ed8;
}

.ai-status--processing > span {
  background: #3b82f6;
}

.ai-status--failed {
  background: #fee2e2;
  color: #b91c1c;
}

.ai-status--failed > span {
  background: #ef4444;
}

.ai-status--expired {
  background: #fef3c7;
  color: #92400e;
}

.ai-status--expired > span {
  background: #f59e0b;
}

.ai-kb__state {
  padding: 2.5rem 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
}

.ai-kb__state--error {
  color: #b91c1c;
}

.ai-kb__state--error button {
  margin-left: 0.5rem;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
}

.ai-kb__footer {
  padding: 0.8rem 1rem;
  border-top: 1px solid #edf0f3;
  color: #64748b;
  font-size: 0.75rem;
}
</style>
